/**
 * Twelve perspective metrics (monthly series).
 */

export function calcRunrateByMonth(revenue) {
  return revenue.map((r) => Math.round((Number(r) || 0) * 12));
}

/**
 * Remaining revenue from month t through forecast end (matches cohort revenue sheet
 * "sum from this column onward" per row).
 */
export function sumCohortRowFromMonth(amounts, fromMonth, N_OUT) {
  let sum = 0;
  for (let u = fromMonth; u < N_OUT; u++) {
    sum += amounts[u] || 0;
  }
  return sum;
}

/** Audit breakdown for one month (debug / validation). */
export function auditRunoffAtMonth(
  t,
  { cohortRevenue, oldCohortRevenueRows = [], N_OUT }
) {
  let newCohorts = 0;
  const newByM = [];
  for (let m = 0; m <= t && m < cohortRevenue.length; m++) {
    const rowSum = sumCohortRowFromMonth(cohortRevenue[m] || [], t, N_OUT);
    newByM.push({ m, rowSum });
    newCohorts += rowSum;
  }

  let oldCohorts = 0;
  const oldByRow = [];
  for (const row of oldCohortRevenueRows) {
    const cohortStart = row.cohortStart;
    if (
      cohortStart == null ||
      !Number.isFinite(cohortStart) ||
      cohortStart > t
    ) {
      continue;
    }
    const rowSum = sumCohortRowFromMonth(row.amounts || [], t, N_OUT);
    oldByRow.push({ cohortStart, rowSum });
    oldCohorts += rowSum;
  }

  return {
    t,
    newCohorts,
    oldCohorts,
    total: newCohorts + oldCohorts,
    newByM,
    oldByRow,
  };
}

/**
 * Future revenue from cohorts active through month t (from month t inclusive).
 */
export function calcRunoffByMonth({
  cohortRevenue,
  oldCohortRevenueRows = [],
  N_OUT,
}) {
  const runoff = new Array(N_OUT).fill(0);

  for (let t = 0; t < N_OUT; t++) {
    for (let m = 0; m <= t && m < cohortRevenue.length; m++) {
      runoff[t] += sumCohortRowFromMonth(cohortRevenue[m] || [], t, N_OUT);
    }

    for (const row of oldCohortRevenueRows) {
      const cohortStart = row.cohortStart;
      if (
        cohortStart == null ||
        !Number.isFinite(cohortStart) ||
        cohortStart > t
      ) {
        continue;
      }
      runoff[t] += sumCohortRowFromMonth(row.amounts || [], t, N_OUT);
    }
  }

  return runoff;
}

/** Exposure as percent (0–100 scale), or null when denominator <= 0. */
export function calcExposurePctByMonth(endBal, denominator) {
  return endBal.map((balance, i) => {
    const denom = Number(denominator[i]) || 0;
    if (denom <= 0) {
      return null;
    }
  const bal = Number(balance) || 0;
    if (bal <= 0) {
      return 0;
    }
    return (bal / denom) * 100;
  });
}

export function calcTwelvePerspectiveMetrics({
  revenue,
  endBal,
  cashEnd,
  cohortRevenue,
  oldCohortRevenueRows,
  N_OUT,
}) {
  const runrate = calcRunrateByMonth(revenue);
  const runoff = calcRunoffByMonth({
    cohortRevenue,
    oldCohortRevenueRows,
    N_OUT,
  });
  const runrateExposurePct = calcExposurePctByMonth(endBal, runrate);
  const runoffExposurePct = calcExposurePctByMonth(endBal, runoff);
  const cashExposurePct = calcExposurePctByMonth(endBal, cashEnd);
  const runoffPlusCashExposurePct = calcExposurePctByMonth(
    endBal,
    (cashEnd ?? []).map((cash, i) => (Number(cash) || 0) + (runoff[i] || 0))
  );

  return {
    runrate,
    runoff,
    runrateExposurePct,
    runoffExposurePct,
    cashExposurePct,
    runoffPlusCashExposurePct,
  };
}
